import { Fragment, useEffect, useState } from 'react';
import { ChecklistItem, Job, JobType, Task, TaskType } from '../../../clients/quipli-service/job-tasks/job-tasks';
import { OrderMedia, OrderMediaType } from '../../../clients/quipli-service/order/order';
import { Button } from '../../../components/form/button';
import { useJobTaskApi } from '../../../clients/quipli-service/job-tasks/job-task-api';
import { Product, ProductInventoryUnit } from '../../../clients/quipli-service/product/product';
import { toast } from '../../../util/toasts';
import { InspectionUnitHeader } from './inspection-view';
import { camelCase, capitalize } from 'lodash';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Spinner } from '../../../components/spinner/spinner';
import './inspection-modal.scss';
import { TextArea } from '../../../components/textarea/textarea';
import classNames from 'classnames';
import { Field, FileField } from '../../../components/field/field';
import { Collapsible } from '../../../components/collapsible/collapsible';
import { MediaCard } from '../../order/view/order-media-list';

type TaskProps = {
  task: Task;
  jobId: string;
  product: Product;
  unit: ProductInventoryUnit;
  unitTrackingVia: 'serialNumber' | 'unitNumber';
  next: () => void;
  back: () => void;
  totalTasks: number;
  isPreview?: boolean;
  setInspection: (job: Job) => void;
};
export const InspectionModalContent = ({
  job,
  product,
  unit,
  unitTrackingVia,
  fuelUnit,
  close,
  setInspection,
}: {
  job: Job;
  product: Product;
  unit: ProductInventoryUnit;
  unitTrackingVia: 'serialNumber' | 'unitNumber';
  fuelUnit: 'gallons' | 'liters';
  close: () => void;
  setInspection: (job: Job) => void;
}) => {
  const { updateTask } = useJobTaskApi();

  const [step, setStep] = useState<number>();
  const [tasks, setTasks] = useState<Task[]>();

  useEffect(() => {
    const checklist = job.tasks.find(
      (t) => t.metadata?.productUnitId === unit.id && t.type === TaskType.InspectionChecklist,
    );
    const hoursTask = job.tasks.find(
      // task only included if unit has hour tracking or fuel product
      (t) => t.metadata?.productUnitId === unit.id && t.type === TaskType.MachineHours,
    );
    const mediaTask = job.tasks.find((t) => t.metadata?.productUnitId === unit.id && t.type === TaskType.MediaUpload);

    if (checklist && mediaTask) {
      setTasks([checklist, ...(hoursTask ? [hoursTask] : []), mediaTask]);
      if (step === undefined) {
        setStep(checklist?.completedAt ? (hoursTask?.completedAt ? 2 : 1) : 0);
      }
    }
    if (checklist && (checklist.status === 'NotStarted' || !checklist.startedAt)) {
      updateTask(job.id, checklist.id, 'InProgress', checklist.metadata ?? {});
    }
  }, [job, unit]);

  // prepare for next task after updateTask is called for the current task
  const next = async () => {
    if (!tasks || step === undefined) return;
    const task = tasks[step];
    if (!task || !task.metadata) return;

    const newStep = step + 1;
    if (task.type === TaskType.MediaUpload) {
      close();
      return;
    }

    if (tasks[newStep]) {
      await updateTask(job.id, tasks[newStep]!.id, 'InProgress', tasks[newStep]!.metadata ?? {});
    }
    setStep(step + 1);
  };

  return tasks && step !== undefined ? (
    <div>
      {tasks[step]?.type === TaskType.InspectionChecklist && (
        <InspectionChecklist
          task={tasks[step]}
          jobId={job.id}
          product={product}
          unit={unit}
          unitTrackingVia={unitTrackingVia}
          next={next}
          back={() => null}
          totalTasks={tasks.length}
          setInspection={setInspection}
        />
      )}
      {tasks[step]?.type === TaskType.MachineHours && (
        <MachineHoursTask
          task={tasks[step]}
          jobId={job.id}
          jobType={job.type}
          product={product}
          unit={unit}
          unitTrackingVia={unitTrackingVia}
          fuelUnit={fuelUnit}
          next={next}
          back={() => setStep(0)}
          totalTasks={tasks.length}
          setInspection={setInspection}
        />
      )}
      {tasks[step]?.type === TaskType.MediaUpload && (
        <MediaUploadTask
          task={tasks[step]}
          jobId={job.id}
          product={product}
          unit={unit}
          unitTrackingVia={unitTrackingVia}
          next={next}
          back={() => setStep(step - 1)}
          totalTasks={tasks.length}
          orderId={parseInt(job.entityId)}
          setInspection={setInspection}
        />
      )}
    </div>
  ) : (
    <Spinner />
  );
};

export const InspectionChecklist = ({
  task,
  jobId,
  product,
  unit,
  unitTrackingVia,
  next,
  back,
  totalTasks,
  isPreview = false,
  setInspection,
}: TaskProps) => {
  const { updateTask } = useJobTaskApi();

  const [checklistValues, setChecklistValues] = useState<{ [key: number]: { value?: boolean; notes?: string } }>(
    // initialize dicitionary with checklist item index mapped to item's value and notes
    task.metadata?.checklist.reduce(
      (acc: { [key: number]: { value?: boolean; notes?: string } }, item: ChecklistItem, i: number) => ({
        ...acc,
        [i]: { value: item.value, notes: item.notes },
      }),
      {} as { [key: number]: { value?: boolean; notes?: string } },
    ),
  );

  const saveTask = async (forward: boolean) => {
    if (isPreview || !task || !task.metadata) return;
    try {
      const updatedJob = await updateTask(jobId, task.id, isTaskComplete(task, product), {
        ...task.metadata,
        status: isTaskComplete(task, product) ? 'Complete' : 'InProgress',
        checklist: task.metadata.checklist.map((item: ChecklistItem, i: number) => ({
          ...item,
          value: checklistValues[i].value,
          notes: checklistValues[i].notes,
        })),
      });
      setInspection(updatedJob);

      if (forward) next();
      else back();
    } catch (e) {
      console.error(e);
      toast('Error updating task', 'error');
    }
  };

  return (
    <div className="inspection-checklist gap-2">
      <Typography className="text-center" type="text-lg">
        {task.header}
      </Typography>
      <InspectionUnitHeader
        product={product}
        unitNumber={unit[unitTrackingVia]}
        progressBar={{ current: 1, total: totalTasks }}
      />
      <Typography type="text-sm" className="text-grey-500 mb-2">
        {task.instructions}
      </Typography>

      {task.metadata?.checklist &&
        task.metadata.checklist.map((item: ChecklistItem, idx: number) => {
          return (
            <div className="checklist-item-wrapper">
              <div className="flex justify-between">
                <Typography type="text-sm" weight="semi-bold">
                  {item.text}
                </Typography>
                <div className="checklist-yes-no flex items-center gap-3">
                  <div
                    className={classNames('hover:bg-green-100 rounded-full thumbs-up', {
                      selected: checklistValues[idx]?.value,
                    })}
                    onClick={() => setChecklistValues({ ...checklistValues, [idx]: { value: true, notes: '' } })}
                  >
                    <Icon icon="Check" stroke={checklistValues[idx]?.value ? 'green' : 'grey'} size="large" />
                  </div>
                  <div
                    className={classNames('hover:bg-red-100 rounded-full thumbs-down', {
                      selected: checklistValues[idx]?.value === false,
                    })}
                    onClick={() =>
                      setChecklistValues({
                        ...checklistValues,
                        [idx]: { value: false, notes: checklistValues[idx]?.notes ?? '' },
                      })
                    }
                  >
                    <Icon
                      className=""
                      icon="X"
                      size="medium"
                      stroke={checklistValues[idx]?.value === false ? 'red' : 'grey'}
                    />
                  </div>
                </div>
              </div>
              {checklistValues[idx]?.value === false && (
                <>
                  <p className="ml-2 text-sm text-grey-500">Provide details</p>
                  <TextArea
                    value={checklistValues[idx]?.notes}
                    onChange={(e) =>
                      setChecklistValues({ ...checklistValues, [idx]: { value: false, notes: e.target.value } })
                    }
                    className="ml-2 mb-2 w-full p-2 border border-grey-200 rounded"
                  />
                </>
              )}
            </div>
          );
        })}
      {!isPreview && <InspectionModalNav step={0} saveTask={saveTask} taskType={TaskType.InspectionChecklist} />}
    </div>
  );
};

export const MachineHoursTask = ({
  task,
  jobId,
  jobType,
  product,
  unit,
  unitTrackingVia,
  fuelUnit,
  next,
  back,
  totalTasks,
  isPreview = false,
  setInspection,
}: TaskProps & { jobType: JobType; fuelUnit: 'gallons' | 'liters' }) => {
  const { updateTask } = useJobTaskApi();
  const [hours, setHours] = useState(task.metadata?.hours ?? unit.lastHourReading?.hour);
  const [fuel, setFuel] = useState(task.metadata?.fuel);

  const saveTask = async (forward: boolean) => {
    // TODO: clean up redundancy...
    if (isPreview || !task || !task.metadata) return;
    try {
      const updatedJob = await updateTask(jobId, task.id, isTaskComplete(task, product), {
        ...task.metadata,
        hours,
        fuel,
      });
      setInspection(updatedJob);
      if (forward) next();
      else back();
    } catch (e) {
      console.error(e);
      toast('Error updating task', 'error');
    }
  };

  return (
    <div className="machine-hours-task-wrapper">
      <Typography className="text-center" type="text-lg">
        {task.header}
      </Typography>
      <InspectionUnitHeader
        product={product}
        unitNumber={unit[unitTrackingVia]}
        progressBar={{ current: 2, total: totalTasks }}
      />
      <Typography type="text-sm" className="text-grey-500 mb-2">
        {task.instructions}
      </Typography>
      <div className="machine-hours-input-wrapper mb-2">
        {product.allowHourTracking && (
          <div className="input-wrapper">
            <Field
              label="Machine Hours"
              type="number"
              value={hours}
              onChange={(e) => setHours(e)}
              className="border border-grey-200 rounded p-2 w-full"
            />
          </div>
        )}
        {jobType === JobType.CheckinInspection && product.fuelChargeProductId && (
          <div className="input-wrapper">
            <Field
              label={`Fuel (${fuelUnit})`}
              type="number"
              value={fuel}
              onChange={(e) => setFuel(e)}
              className="border border-grey-200 rounded p-2 w-full"
            />
          </div>
        )}
      </div>
      {!isPreview && <InspectionModalNav step={1} saveTask={saveTask} taskType={TaskType.MachineHours} />}
    </div>
  );
};

export const MediaUploadTask = ({
  task,
  jobId,
  product,
  unit,
  orderId,
  unitTrackingVia,
  next,
  back,
  totalTasks,
  isPreview = false,
  setInspection,
}: TaskProps & { orderId: number }) => {
  const { updateTask } = useJobTaskApi();
  const { getUploadOrderMediaUrl, addOrderMedia, listOrderMedia } = useOrderApiV1();
  const { uploadToS3 } = useAPI();

  const [isUploading, setIsUploading] = useState(false);
  const [media, setMedia] = useState(
    task.metadata?.media ??
      task.metadata?.mediaSections.reduce(
        (acc: Record<string, string[]>, section: string) => ({ ...acc, [camelCase(section)]: [] }),
        {} as Record<string, string[]>,
      ),
  );
  const [mediaList, setMediaList] = useState<OrderMedia[]>();

  const fetch = async () => {
    setIsUploading(true);

    try {
      setMediaList(await listOrderMedia(orderId));
    } catch (error: any) {
      toast(error.message.msg || error.message, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const saveTask = async (forward: boolean) => {
    if (isPreview || !task || !task.metadata) return;
    try {
      const updatedJob = await updateTask(jobId, task.id, isTaskComplete(task, product), {
        ...task.metadata,
        media: { ...task.metadata.media, ...media },
      });
      setInspection(updatedJob);
      if (forward) next();
      else back();
    } catch (e) {
      console.error(e);
      toast('Error updating task', 'error');
    }
  };

  const upload = async (section: string, files: File[]) => {
    if (isPreview || files.length === 0 || isUploading) return;

    setIsUploading(true);

    for (const file of files) {
      const type = file.type.match('image/*') ? OrderMediaType.Image : OrderMediaType.Video;

      // if (file.size > 8 * 1024 * 1024) {
      //   toast(`Error uploading ${file.name}: Size must be less than 8MB`, 'error');
      //   continue;
      // }

      try {
        const { url: preSignedUrl, mediaId } = await getUploadOrderMediaUrl(
          orderId,
          `Unit ${unit[unitTrackingVia]} - ${capitalize(section)} | ${file.name}`,
          type,
        );
        // Save to S3
        await uploadToS3(preSignedUrl, file);
        // Save to DB
        await addOrderMedia(orderId, mediaId, file.name, type);
        const updatedMedia = { ...media, [section]: [...media[section], mediaId] };
        setMedia(updatedMedia);
        const updatedJob = await updateTask(jobId, task.id, 'InProgress', {
          ...task.metadata,
          media: { ...(task.metadata?.media ?? {}), ...updatedMedia },
        });
        setInspection(updatedJob);
        await fetch();
        toast(`${file.name} uploaded successfully.`);
      } catch (error: any) {
        console.error(error);
        toast(
          `Error uploading ${file.name}: ${error.message.msg || error.message || error.msg || 'Something went wrong.'}`,
          'error',
        );
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <div className="media-upload-task-wrapper">
      <Typography className="text-center" type="text-lg">
        {task.header}
      </Typography>
      <InspectionUnitHeader
        product={product}
        unitNumber={unit[unitTrackingVia]}
        progressBar={{ current: 3, total: totalTasks }}
      />
      <Typography type="text-sm" className="text-grey-500 mb-2">
        {task.instructions}
      </Typography>
      <div className="media-sections-wrapper mt-2">
        {mediaList !== undefined ? (
          task.metadata?.mediaSections.map((section: string) => (
            <MediaSection
              key={section}
              list={mediaList.filter((m) => (media[camelCase(section)] ?? []).indexOf(m.id) >= 0)}
              isListLoading={false}
              refresh={fetch}
              upload={(files) => upload(camelCase(section), files)}
              section={section}
            />
          ))
        ) : (
          <Spinner />
        )}
      </div>
      {!isPreview && <InspectionModalNav step={2} saveTask={saveTask} taskType={TaskType.MediaUpload} />}
    </div>
  );
};

const InspectionModalNav = ({
  step,
  saveTask,
  taskType,
}: {
  step: number;
  saveTask: (forward: boolean) => void;
  taskType: TaskType;
}) => {
  return (
    <div>
      <div className="button-nav flex justify-between gap-3 mt-4">
        <Button
          disabled={step === 0}
          className="w-full button button-secondary"
          onClick={() => {
            saveTask(false);
          }}
        >
          Back
        </Button>
        <Button
          className="w-full button button-yellow"
          onClick={() => {
            saveTask(true);
          }}
        >
          {taskType === TaskType.MediaUpload ? 'Save' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

const isTaskComplete = (task: Task, product: Product) => {
  if (!task || !task.metadata) return 'NotStarted';
  if (task.type === TaskType.InspectionChecklist) {
    return task.metadata?.checklist.every((item: ChecklistItem) => (item.required ? item.value !== undefined : true))
      ? 'Complete'
      : 'InProgress';
  } else if (task.type === TaskType.MachineHours) {
    return (!product.allowHourTracking || (product.allowHourTracking && task.metadata?.hours !== undefined)) &&
      ((product.fuelChargeProductId && task.metadata?.fuel !== undefined) || !product.fuelChargeProductId)
      ? 'Complete'
      : 'InProgress';
  } else {
    return task.metadata?.media && Object.keys(task.metadata.media).some((m) => m.length) ? 'Complete' : 'InProgress';
  }
};

export const MediaSection = ({
  list,
  isListLoading,
  refresh,
  upload,

  section,
}: {
  list: OrderMedia[];
  isListLoading: boolean;
  refresh: () => void;
  upload: (files: File[]) => void;

  section: string;
}) => {
  return (
    <div className="media-section">
      <Collapsible
        trigger={(isOpen) => (
          <div className="flex items-center gap-2 my-1">
            <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" onClick={() => 0} />
            <Typography weight="bold" type="text-sm">
              {capitalize(section)}
            </Typography>
          </div>
        )}
        openAtStart={true}
      >
        <div
          className={classNames('flex overflow-x-auto gap-[8px] w-full', {
            'justify-center': isListLoading,
            'mt-[8px] py-[4px]': list.length > 0,
          })}
        >
          <div className="upload-square ">
            <FileField
              multiple
              accept="image/jpeg, image/png, video/mp4, video/quicktime"
              value={undefined}
              onChange={(files) => upload(files as File[])}
              icon="Plus"
              showClickToUploadText={false}
            />
          </div>

          {isListLoading || !list ? (
            <Spinner />
          ) : (
            list.map((media, idx) => (
              <Fragment key={`order-media-${idx}`}>
                <MediaCard media={media} reload={refresh} />
              </Fragment>
            ))
          )}
        </div>
      </Collapsible>
    </div>
  );
};
